// src/pages/CommissionsPage.js
// หน้าแสดงคอมมิชชั่น v3.0
// - เอาฟีเจอร์จ่ายเงินออก
// - เพิ่ม Pagination
// - เปลี่ยน Filter เป็นเลือกเดือน/ปี

import React, { useEffect, useState, useContext, useMemo } from "react";
import api from "../api";
import AuthContext from "../AuthContext";
import { useNotification } from "../NotificationContext";
import SkeletonLoader from "../components/SkeletonLoader";
// Compact chart for commissions by employee
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const CommissionsPage = () => {
  const { user } = useContext(AuthContext);
  const { addNotification } = useNotification();

  const [employees, setEmployees] = useState([]);

  // Filter state
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedEmpId, setSelectedEmpId] = useState("");

  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const canManage = isAdmin || isManager; // admin or manager

  // popup ดูบิลจากเลขที่บิล
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderDetail, setOrderDetail] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  // โหลดรายชื่อพนักงาน (admin/manager)
  const loadEmployees = async () => {
    if (!canManage) return;
    try {
      const res = await api.get("/employees");
      setEmployees(res.data || []);
    } catch (err) {
      console.error(err);
      setError("โหลดข้อมูลพนักงานไม่สำเร็จ");
    }
  };

  // Helper: Get local date string YYYY-MM-DD (not UTC)
  const getLocalDateString = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // คำนวณ date_from และ date_to จากเดือน/ปีที่เลือก
  const getDateRange = () => {
    const startDate = new Date(selectedYear, selectedMonth - 1, 1);
    // If current month, use today as end date; otherwise use last day of month
    const isCurrentMonth = selectedYear === currentYear && selectedMonth === currentMonth;
    const endDate = isCurrentMonth ? new Date() : new Date(selectedYear, selectedMonth, 0);

    const date_from = getLocalDateString(startDate);
    const date_to = getLocalDateString(endDate);

    return { date_from, date_to };
  };

  // โหลดคอมมิชชั่น
  const loadCommissions = async () => {
    setLoading(true);
    setError("");

    try {
      const { date_from, date_to } = getDateRange();

      const params = {
        date_from,
        date_to,
      };

      // ถ้า admin/manager: ใช้ emp_id จาก filter (ถ้าเลือก)
      // ถ้า employee: จำกัดเฉพาะของตัวเอง
      if (canManage) {
        if (selectedEmpId) params.emp_id = selectedEmpId;
      } else if (user?.employee_id) {
        params.emp_id = user.employee_id;
      }

      const res = await api.get("/commissions", { params });
      setCommissions(res.data || []);
      setCurrentPage(1); // Reset to first page on new search
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        setError(
          "เซสชันหมดอายุหรือ token ไม่ถูกต้อง กรุณาออกจากระบบแล้วเข้าสู่ระบบใหม่"
        );
      } else {
        setError("โหลดข้อมูลคอมมิชชั่นไม่สำเร็จ");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    loadCommissions();
  }, []);

  const handleSearch = () => {
    loadCommissions();
  };

  const handleResetFilters = () => {
    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
    setSelectedEmpId("");
    setTimeout(() => loadCommissions(), 50); // Small delay to ensure state update
  };

  const formatDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("th-TH");
    } catch {
      return d;
    }
  };

  const formatDateTime = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      const dateStr = dt.toLocaleDateString("th-TH");
      const timeStr = dt.toLocaleTimeString("th-TH", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${dateStr} ${timeStr}`;
    } catch {
      return d;
    }
  };

  const formatMoney = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // --------- Aggregation ---------
  const agg = useMemo(() => {
    const byEmp = new Map();
    let total = 0;

    for (const c of commissions) {
      const empKey = c.emp_id ?? c.emp_name ?? "-";
      const name = c.emp_name || `รหัส ${empKey}`;
      const amt = Number(c.commission_amount || 0);
      total += amt;

      if (!byEmp.has(empKey)) byEmp.set(empKey, { name, amount: 0 });
      byEmp.get(empKey).amount += amt;
    }
    // sort desc
    const rows = Array.from(byEmp.values()).sort((a, b) => b.amount - a.amount);
    const labels = rows.map(r => r.name);
    const values = rows.map(r => Number(r.amount.toFixed(2)));
    return {
      labels, values, rows,
      totals: { total, employees: byEmp.size, items: commissions.length }
    };
  }, [commissions]);

  const chartData = useMemo(() => ({
    labels: agg.labels,
    datasets: [
      {
        label: "คอมมิชชั่น (บาท)",
        data: agg.values,
        backgroundColor: "rgba(212, 175, 55, 0.6)", // Gold theme
        borderColor: "rgba(212, 175, 55, 1)",
        borderWidth: 1,
        barThickness: 14,
      },
    ],
  }), [agg.labels, agg.values]);

  const chartOptions = useMemo(() => ({
    indexAxis: 'y', // horizontal bar to fit many employees
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: 'nearest', intersect: false } },
    scales: {
      x: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
      y: { grid: { display: false } },
    },
  }), []);

  // ---------------------- Pagination Logic ----------------------
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = commissions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(commissions.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // ---------------------- ดูบิลจากเลขที่บิล (popup) ----------------------

  const openOrderModal = async (orderId) => {
    if (!orderId) return;
    setOrderModalOpen(true);
    setOrderDetail(null);
    setLoadingOrder(true);
    try {
      const res = await api.get(`/orders/${orderId}`);
      setOrderDetail(res.data);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "โหลดรายละเอียดบิลจากคอมมิชชั่นไม่สำเร็จ"
      );
    } finally {
      setLoadingOrder(false);
    }
  };

  const closeOrderModal = () => {
    setOrderModalOpen(false);
    setOrderDetail(null);
  };

  const orderModalOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.4)",
    zIndex: 1050,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const orderModalDialogStyle = {
    backgroundColor: "#fff",
    borderRadius: "1rem",
    maxWidth: "900px",
    width: "96%",
    maxHeight: "92vh",
    overflowY: "auto",
  };

  const getOrderTimeText = (o) => {
    if (!o) return "-";
    const dt = o.server_time || o.created_at;
    if (!dt) return "-";
    return formatDateTime(dt);
  };

  return (
    <div className="dashboard-page commissions-page container">
      {/* Hero */}
      <div className="dashboard-hero mb-3">
        <div>
          <div className="dashboard-hero-title">
            <i className="bi bi-percent me-2"></i>คอมมิชชั่น
          </div>
          <div className="dashboard-hero-subtitle">
            ตรวจสอบยอดคอมมิชชั่นตามบิลที่ขายจริง
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* ฟิลเตอร์ */}
      <div className="card card-ks-soft mb-3">
        <div className="card-body">
          <h5 className="mb-3">ตัวกรองคอมมิชชั่น</h5>
          <div className="row g-3">
            {canManage && (
              <div className="col-md-3 col-sm-6">
                <label className="form-label form-label-sm">พนักงาน</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedEmpId}
                  onChange={(e) => setSelectedEmpId(e.target.value)}
                >
                  <option value="">— ทั้งหมด —</option>
                  {employees.map((e) => (
                    <option key={e.emp_id} value={e.emp_id}>
                      {e.emp_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-md-2 col-sm-6">
              <label className="form-label form-label-sm">เดือน</label>
              <select
                className="form-select form-select-sm"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('th-TH', { month: 'long' })}</option>
                ))}
              </select>
            </div>

            <div className="col-md-2 col-sm-6">
              <label className="form-label form-label-sm">ปี</label>
              <select
                className="form-select form-select-sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map(y => (
                  <option key={y} value={y}>{y + 543}</option>
                ))}
              </select>
            </div>

            <div className="col-md-3 col-sm-12 d-flex align-items-end gap-2">
              <button
                className="btn btn-ks-success btn-sm px-3"
                type="button"
                onClick={handleSearch}
              >
                <i className="fas fa-search me-1"></i>
                ค้นหา
              </button>
              <button
                className="btn btn-ks-outline btn-sm px-3"
                type="button"
                onClick={handleResetFilters}
              >
                ล้างตัวกรอง
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* กราฟรวมต่อพนักงาน (ซ้าย) + สรุป (ขวา) */}
      <div className="card card-ks-soft mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-stretch">
            <div className="col-lg-8 col-md-7">
              <h6 className="mb-2">คอมมิชชั่นรวมตามพนักงาน (ประจำเดือนนี้)</h6>
              <div style={{ height: Math.min(320, Math.max(180, 24 + (agg.labels.length * 22))) }} className="bg-white border rounded p-2">
                {agg.labels.length === 0 ? (
                  <div className="text-muted small">ไม่มีข้อมูลคอมมิชชั่นในช่วงที่เลือก</div>
                ) : (
                  <Bar data={chartData} options={chartOptions} />
                )}
              </div>
            </div>
            <div className="col-lg-4 col-md-5">
              <h6 className="mb-2">สรุปภาพรวม</h6>
              <div className="bg-white border rounded p-3">
                <div className="d-flex justify-content-between"><span className="text-muted">จำนวนพนักงาน</span><strong>{agg.totals.employees.toLocaleString()} คน</strong></div>
                <div className="d-flex justify-content-between"><span className="text-muted">จำนวนรายการ</span><strong>{agg.totals.items.toLocaleString()} รายการ</strong></div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between"><span>รวมคอมมิชชั่นทั้งหมด</span><strong style={{ color: "#d4af37" }} className="fs-5">{formatMoney(agg.totals.total)} ฿</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ตารางคอมมิชชั่น */}
      <div className="card card-ks-soft">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h5 className="mb-0">
                รายการคอมมิชชั่น{" "}
                {loading && (
                  <span
                    className="text-muted"
                    style={{ fontSize: "0.85rem" }}
                  >
                    (กำลังโหลด...)
                  </span>
                )}
              </h5>
              <div className="small text-muted">
                แสดงรายการที่ {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, commissions.length)} จากทั้งหมด {commissions.length} รายการ
              </div>
            </div>
            <div>
              <label className="form-label form-label-sm text-muted mb-0 me-2">แถวต่อหน้า</label>
              <select className="form-select form-select-sm d-inline-block" style={{ width: 100 }} value={itemsPerPage} onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {loading ? (
            <SkeletonLoader type="table" rows={8} columns={4} />
          ) : (
            <div className="table-responsive mt-2">
              <table className="table table-sm table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>เลขที่บิล</th>
                    <th>วันเวลา ทำรายการ</th>
                    <th>พนักงาน</th>
                    <th className="text-end">ยอดคอมมิชชั่น</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-4">
                        ไม่พบข้อมูลคอมมิชชั่น
                      </td>
                    </tr>
                  )}
                  {currentItems.map((c) => (
                    <tr key={c.comm_id}>
                      {/* เลขที่บิล → กดเพื่อเปิด popup */}
                      <td>
                        {c.order_id ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => openOrderModal(c.order_id)}
                          >
                            <span className="badge bg-light text-dark border">{c.order_code}</span>
                          </button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>

                      <td>{formatDateTime(c.order_time || c.trans_date)}</td>
                      <td>{c.emp_name || "-"}</td>
                      <td className="text-end fw-bold" style={{ color: "#d4af37" }}>
                        {formatMoney(c.commission_amount || 0)} ฿
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(currentPage - 1)}>&laquo; ก่อนหน้า</button>
                </li>

                {/* แสดงหน้าแรกเสมอ */}
                {currentPage > 3 && (
                  <>
                    <li className="page-item">
                      <button className="page-link" onClick={() => paginate(1)}>1</button>
                    </li>
                    {currentPage > 4 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                  </>
                )}

                {/* แสดงหน้าใกล้เคียง (current -2 ถึง +2) */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(number => number >= currentPage - 2 && number <= currentPage + 2)
                  .map(number => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => paginate(number)}>{number}</button>
                    </li>
                  ))}

                {/* แสดงหน้าสุดท้ายเสมอ */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                    <li className="page-item">
                      <button className="page-link" onClick={() => paginate(totalPages)}>{totalPages}</button>
                    </li>
                  </>
                )}

                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => paginate(currentPage + 1)}>ถัดไป &raquo;</button>
                </li>
              </ul>
            </nav>
          )}

        </div>
      </div>

      {/* POPUP: รายละเอียดบิลจากเลขที่บิลของคอมมิชชั่น */}
      {orderModalOpen && (
        <div style={orderModalOverlayStyle} onClick={closeOrderModal}>
          <div
            style={orderModalDialogStyle}
            className="card card-ks-soft shadow-soft p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <h5 className="mb-0">
                  รายละเอียดบิล{" "}
                  {orderDetail ? orderDetail.order_code : ""}
                </h5>
                <div className="small text-muted">
                  ตรวจสอบยอดขายและส่วนลดที่เกี่ยวข้องกับคอมมิชชั่นนี้
                </div>
              </div>
              <button
                type="button"
                className="btn btn-ks-outline btn-sm"
                onClick={closeOrderModal}
              >
                ปิด
              </button>
            </div>

            {loadingOrder ? (
              <div>กำลังโหลดรายละเอียดบิล...</div>
            ) : !orderDetail ? (
              <div className="text-muted">
                ไม่พบข้อมูลบิลสำหรับเลขที่นี้
              </div>
            ) : (
              <>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <dl className="row mb-0 small">
                      <dt className="col-sm-4">เลขบิล</dt>
                      <dd className="col-sm-8">
                        {orderDetail.order_code}
                      </dd>

                      <dt className="col-sm-4">สถานะ</dt>
                      <dd className="col-sm-8">
                        {orderDetail.status}
                      </dd>

                      <dt className="col-sm-4">ลูกค้า</dt>
                      <dd className="col-sm-8">
                        {orderDetail.customer_name || "-"}
                      </dd>

                      <dt className="col-sm-4">เบอร์โทร</dt>
                      <dd className="col-sm-8">
                        {orderDetail.customer_phone || "-"}
                      </dd>

                      <dt className="col-sm-4">เวลา</dt>
                      <dd className="col-sm-8">
                        {getOrderTimeText(orderDetail)}
                      </dd>
                    </dl>
                  </div>
                  <div className="col-md-6">
                    <dl className="row mb-0 small">
                      <dt className="col-sm-5">ยอดก่อนส่วนลด</dt>
                      <dd className="col-sm-7">
                        {formatMoney(orderDetail.subtotal)} ฿
                      </dd>
                      <dt className="col-sm-5">ส่วนลดรวม</dt>
                      <dd className="col-sm-7">
                        {formatMoney(orderDetail.discount_amount || 0)} ฿
                      </dd>
                      <dt className="col-sm-5">ยอดสุทธิ</dt>
                      <dd className="col-sm-7">
                        {formatMoney(orderDetail.total_amount)} ฿
                      </dd>
                    </dl>
                  </div>
                </div>

                <h6 className="mb-2">รายการบริการในบิลนี้</h6>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>บริการ</th>
                        <th style={{ width: 70 }}>จำนวน</th>
                        <th>พนักงานหลัก</th>
                        <th>ผู้ช่วย</th>
                        <th className="text-end">ส่วนลด</th>
                        <th className="text-end">ยอดสุทธิ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetail.items.map((it) => (
                        <tr key={it.trans_id}>
                          <td>{it.service_name}</td>
                          <td>{it.qty}</td>
                          <td>{it.main_emp_name || "-"}</td>
                          <td>
                            {it.extra_employees &&
                              it.extra_employees.length > 0
                              ? it.extra_employees
                                .map(
                                  (e) =>
                                    `${e.emp_name || ""}${e.share_percent
                                      ? ` (${e.share_percent}%)`
                                      : ""
                                    }`
                                )
                                .join(", ")
                              : "-"}
                          </td>
                          <td className="text-end">
                            {formatMoney(it.line_discount || 0)} ฿
                          </td>
                          <td className="text-end">
                            {formatMoney(it.amount)} ฿
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsPage;
